import { useState, useContext, useEffect, useRef } from 'react'

import proj4 from 'proj4'
import { register } from 'ol/proj/proj4'
import GeoJSON from 'ol/format/GeoJSON'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import { Fill, Stroke, Style, Circle as CircleStyle } from 'ol/style'

import MapContext from '../Map/MapContext'
import Tool from '../Tool/Tool'
import TabNavigation from '../Bootstrap/Tabs/TabNavigation'
import Modal from '../Bootstrap/Modal/Modal'
import { getTool } from '../../utils/toolsHelper'
import { useGlobalVariablesContext } from '../../context/GlobalVariablesContext'
import { useToolsContext } from '../../context/ToolsContext'

import './CenterOnFeature.css'

// Register common projections once at module level
proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs')
proj4.defs('EPSG:25830', '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs')
proj4.defs('EPSG:25829', '+proj=utm +zone=29 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs')
proj4.defs('EPSG:23030', '+proj=utm +zone=30 +ellps=clrk66 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs')
register(proj4)

const HIGHLIGHT_LAYER_NAME = 'centerOnFeature_highlight'
const TOOL_TITLE = 'Búsqueda por campo'

// Caché de features por sesión: evita re-fetchar al abrir/cerrar el panel
const featuresCache = new Map()

// ── SearchItem ─────────────────────────────────────────────────────────────────

const SearchItem = ({ search, searchKey, onCenter, showLabel = true, isActive, forceEnableClear = false }) => {
    const [options, setOptions] = useState([])
    const [inputValue, setInputValue] = useState('')
    const [loading, setLoading] = useState(false)
    const [errorMsg, setErrorMsg] = useState('')
    const wasActiveRef = useRef(isActive)

    const { wfsUrl, layer, displayField } = search
    const maxFeatures = search.maxFeatures || 2000
    const datalistId = `cof-datalist-${searchKey}`
    const inputId = `cof-input-${searchKey}`

    useEffect(() => {
        if (wasActiveRef.current && !isActive) {
            setInputValue('')
            onCenter(null)
        }
        wasActiveRef.current = isActive
    }, [isActive])

    useEffect(() => {
        if (!wfsUrl || !layer || !displayField) return

        const cacheKey = `${wfsUrl}|${layer}|${displayField}|${maxFeatures}`
        if (featuresCache.has(cacheKey)) {
            setOptions(featuresCache.get(cacheKey))
            return
        }

        setLoading(true)
        setErrorMsg('')

        const url = `${wfsUrl}?service=WFS&version=2.0.0&request=GetFeature` +
            `&typeName=${layer}&outputFormat=application/json&count=${maxFeatures}&srsName=EPSG:4326`

        fetch(url)
            .then(res => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`)
                return res.json()
            })
            .then(data => {
                const format = new GeoJSON()
                const features = format.readFeatures(data, {
                    dataProjection: 'EPSG:4326',
                    featureProjection: 'EPSG:3857'
                })

                const sorted = features
                    .filter(f => f.get(displayField) != null)
                    .sort((a, b) =>
                        String(a.get(displayField)).localeCompare(String(b.get(displayField)))
                    )

                featuresCache.set(cacheKey, sorted)
                setOptions(sorted)
            })
            .catch(err => {
                console.error('CenterOnFeature: WFS fetch error', err)
                setErrorMsg('Error al cargar los datos')
            })
            .finally(() => setLoading(false))
    }, [wfsUrl, layer, displayField, maxFeatures])

    const handleChange = (e) => {
        const value = e.target.value
        setInputValue(value)
        const selected = options.find(f => String(f.get(displayField)) === value)
        if (selected) onCenter(selected)
    }

    const handleClear = () => {
        setInputValue('')
        onCenter(null)
    }

    return (
        <div className='cof-search-item'>
            {loading && (
                <p className='text-muted small mt-1'>Cargando...</p>
            )}
            {errorMsg && (
                <p className='text-danger small mt-1'>{errorMsg}</p>
            )}
            {!loading && !errorMsg && (
                <>
                    {showLabel && (
                        <label className='fw-bold mb-1' htmlFor={inputId}>
                            {search.inputLabel || displayField}:
                        </label>
                    )}
                    <input
                        id={inputId}
                        className='form-control mt-1'
                        list={datalistId}
                        value={inputValue}
                        onChange={handleChange}
                        placeholder='Selecciona o escribe...'
                        autoComplete='off'
                    />
                    <datalist id={datalistId}>
                        {options.map((f, i) => (
                            <option key={i} value={String(f.get(displayField))} />
                        ))}
                    </datalist>
                    <p className='cof-count text-muted'>{options.length} elementos</p>
                    <button
                        className='btn btn-secondary w-100 mt-1'
                        onClick={handleClear}
                        disabled={!inputValue && !forceEnableClear}
                    >
                        Limpiar búsqueda
                    </button>
                </>
            )}
        </div>
    )
}

// ── CenterOnFeature ────────────────────────────────────────────────────────────

const CenterOnFeature = () => {
    const { map } = useContext(MapContext)
    const { config } = useGlobalVariablesContext()
    const { toolStates } = useToolsContext()
    const isCofOpen = toolStates['centerOnFeature']?.sidebarOpen ?? false
    const highlightLayerRef = useRef(null)
    const modalOpenRef = useRef(false)
    const [activeTab, setActiveTab] = useState('callejero')
    const [otrosSelectedIndex, setOtrosSelectedIndex] = useState(0)
    const [highlightedKey, setHighlightedKey] = useState(null)

    const tool = getTool(config?.TOOLS, 'centerOnFeature')
    const conf = tool?.params
    const [selectedFeature, setSelectedFeature] = useState(null)

    // Backward compat: old single-search format or old searches[] → otros
    const otrosSearches = (() => {
        if (!conf) return []
        if (conf.searches?.length > 0) return conf.searches
        if (conf.layer) {
            return [{
                wfsUrl: conf.wfsUrl || '',
                layer: conf.layer || '',
                displayField: conf.displayField || '',
                maxFeatures: conf.maxFeatures || 2000,
                inputLabel: conf.inputLabel || ''
            }]
        }
        return []
    })()

    if (!tool) return null

    const callejeroSearch = (conf?.callejero && conf.callejero.enable !== false && conf.callejero.layer)
        ? conf.callejero : null
    const poblacionesSearch = (conf?.poblaciones && conf.poblaciones.enable !== false && conf.poblaciones.layer)
        ? conf.poblaciones : null

    const tabs = [
        ...(callejeroSearch ? [{ id: 'callejero', label: conf.callejero.inputLabel || 'Callejero' }] : []),
        ...(poblacionesSearch ? [{ id: 'poblaciones', label: conf.poblaciones.inputLabel || 'Poblaciones' }] : []),
        ...(otrosSearches.length > 0 ? [{ id: 'otros', label: 'Otras' }] : []),
    ]

    const currentTab = tabs.find(t => t.id === activeTab)?.id ?? tabs[0]?.id ?? null

    useEffect(() => {
        if (!isCofOpen) {
            removeHighlightLayer()
            setHighlightedKey(null)
        }
    }, [isCofOpen])

    const removeHighlightLayer = () => {
        if (!map) return
        map.getLayers().forEach(l => {
            if (l && l.get('name') === HIGHLIGHT_LAYER_NAME) map.removeLayer(l)
        })
        highlightLayerRef.current = null
    }

    useEffect(() => {
        if (!map) return

        const handleClick = (event) => {
            if (!highlightLayerRef.current) return
            map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
                if (layer === highlightLayerRef.current) {
                    setSelectedFeature(feature)
                    return true
                }
            })
        }

        const setMapCursor = (cursor) => {
            const el = map.getTargetElement()
            if (el) el.style.cursor = cursor
        }

        const handlePointerMove = (event) => {
            if (modalOpenRef.current) return
            if (!highlightLayerRef.current) {
                setMapCursor('')
                return
            }
            const hit = map.hasFeatureAtPixel(event.pixel, { layerFilter: l => l === highlightLayerRef.current })
            setMapCursor(hit ? 'pointer' : '')
        }

        // map.on('click', handleClick)
        map.on('pointermove', handlePointerMove)
        return () => {
            // map.un('click', handleClick)
            map.un('pointermove', handlePointerMove)
            setMapCursor('')
        }
    }, [map])

    useEffect(() => {
        modalOpenRef.current = !!selectedFeature
    }, [selectedFeature])

    const handleCloseModal = () => {
        setSelectedFeature(null)
    }

    const handleCenter = (feature, key) => {
        removeHighlightLayer()
        setSelectedFeature(null)
        if (!feature || !map) {
            setHighlightedKey(null)
            return
        }

        const vectorSource = new VectorSource({ features: [feature] })
        const vectorLayer = new VectorLayer({
            source: vectorSource,
            zIndex: 1100,
            style: new Style({
                fill: new Fill({ color: 'rgba(0, 100, 255, 0.15)' }),
                stroke: new Stroke({ color: 'rgba(0, 100, 255, 0.85)', width: 2.5 }),
                image: new CircleStyle({
                    radius: 8,
                    fill: new Fill({ color: 'rgba(0, 100, 255, 0.5)' }),
                    stroke: new Stroke({ color: 'rgba(0, 100, 255, 0.85)', width: 2.5 })
                })
            })
        })

        vectorLayer.set('name', HIGHLIGHT_LAYER_NAME)
        vectorLayer.set('title', TOOL_TITLE)
        vectorLayer.set('queryable', true)

        map.addLayer(vectorLayer)
        highlightLayerRef.current = vectorLayer
        setHighlightedKey(key)

        const extent = vectorSource.getExtent()
        map.getView().fit(extent, {
            padding: [80, 80, 80, 80],
            duration: 1000,
            maxZoom: 18
        })
    }

    const handleTabChange = (tabId) => {
        if (tabId === activeTab) return
        setActiveTab(tabId)
        removeHighlightLayer()
        setHighlightedKey(null)
    }

    const toolTitle = conf?.toolTitle || TOOL_TITLE

    const sidebarContent = (
        <div className='center-on-feature'>
            {tabs.length === 0 && (
                <p className='text-muted small'>No hay búsquedas configuradas</p>
            )}

            {tabs.length > 1 && (
                <TabNavigation
                    activeTab={currentTab}
                    setActiveTab={handleTabChange}
                    tabs={tabs.map(t => ({ label: t.label, value: t.id }))}
                />
            )}

            {callejeroSearch && (
                <div style={{ display: currentTab === 'callejero' ? 'block' : 'none' }}>
                    <SearchItem
                        search={callejeroSearch}
                        searchKey='callejero'
                        onCenter={(f) => handleCenter(f, 'callejero')}
                        showLabel={false}
                        isActive={isCofOpen && currentTab === 'callejero'}
                        forceEnableClear={highlightedKey === 'callejero'}
                    />
                </div>
            )}

            {poblacionesSearch && (
                <div style={{ display: currentTab === 'poblaciones' ? 'block' : 'none' }}>
                    <SearchItem
                        search={poblacionesSearch}
                        searchKey='poblaciones'
                        onCenter={(f) => handleCenter(f, 'poblaciones')}
                        showLabel={false}
                        isActive={isCofOpen && currentTab === 'poblaciones'}
                        forceEnableClear={highlightedKey === 'poblaciones'}
                    />
                </div>
            )}

            {otrosSearches.length > 0 && (
                <div style={{ display: currentTab === 'otros' ? 'block' : 'none' }}>
                    {otrosSearches.length > 1 && (
                        <>
                            <p className='small text-muted mb-1'>Selecciona el campo de búsqueda:</p>
                            <select
                                className='form-select mb-3'
                                value={otrosSelectedIndex}
                                onChange={e => setOtrosSelectedIndex(parseInt(e.target.value, 10))}
                            >
                                {otrosSearches.map((s, i) => (
                                    <option key={i} value={i}>
                                        {s.inputLabel || s.layer || `Búsqueda ${i + 1}`}
                                    </option>
                                ))}
                            </select>
                        </>
                    )}
                    {otrosSearches.map((search, i) => (
                        <div key={i} style={{ display: i === otrosSelectedIndex ? 'block' : 'none' }}>
                            <SearchItem
                                search={search}
                                searchKey={i}
                                onCenter={(f) => handleCenter(f, `otros-${i}`)}
                                showLabel={otrosSearches.length === 1}
                                isActive={isCofOpen && currentTab === 'otros' && i === otrosSelectedIndex}
                                forceEnableClear={highlightedKey === `otros-${i}`}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    )

    const toolIcon = (
        <svg strokeWidth="2" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <path d="M11.5 20H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5.5M9 17h2" />
            <path d="M15 18a3 3 0 1 0 6 0 3 3 0 1 0-6 0M20.2 20.2 22 22" />
        </svg>
    )

    const featureAttrs = selectedFeature
        ? Object.entries(selectedFeature.getProperties()).filter(
            ([, v]) => v !== null && v !== undefined && typeof v !== 'object'
        )
        : []

    return (
        <>
            <Tool
                id='centerOnFeature'
                type='toggleable'
                iconNode={toolIcon}
                label={toolTitle}
                sidebarTitle={toolTitle}
                hasSidebar
                sidebarContent={sidebarContent}
            />
            <Modal
                show={!!selectedFeature}
                onClose={handleCloseModal}
                title='Atributos'
                id='cof-feature-modal'
            >
                <div className='table-responsive'>
                    <table className='table table-sm table-striped mb-0'>
                        <tbody>
                            {featureAttrs.map(([key, value]) => (
                                <tr key={key}>
                                    <th className='text-nowrap pe-3' style={{ fontWeight: 600, width: '40%' }}>{key}</th>
                                    <td>{String(value)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Modal>
        </>
    )
}

export default CenterOnFeature
